import { useState, useEffect } from 'react'
import SEOHead from '../components/SEOHead'
import Lightbox from '../components/Lightbox'
import { useCmsPage } from '../hooks/useCmsPage'

function getOptimizedImageUrl(url, width = 800) {
  if (!url || !url.includes('assets.auxodev.dk')) return url
  if (url.includes('/cdn-cgi/image/')) return url
  return url.replace(
    'https://assets.auxodev.dk/',
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`
  )
}

export default function GalleriPage() {
  const { page } = useCmsPage('galleri')
  const [galleries, setGalleries] = useState([])
  const [activeCategory, setActiveCategory] = useState('alle')
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [loading, setLoading] = useState(true)

  const title = page?.meta_title || 'GALLERI — Casper DJ'
  const description =
    page?.meta_description ||
    'Oplev billeder og stemningsindtryk fra Casper DJs optrædener ved firmafester, privatfester og bryllupper.'

  useEffect(() => {
    async function fetchAllGalleries() {
      try {
        setLoading(true)
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/galleries?limit=20')
        if (res.ok) {
          const data = await res.json()
          if (data.items) {
            setGalleries(data.items)
          }
        }
      } catch (err) {
        console.error('Fejl ved hentning af gallerier:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchAllGalleries()
  }, [])

  // Aggregate images based on activeCategory
  const currentGallery = galleries.find((g) => {
    const slug = typeof g.slug === 'object' ? g.slug?._ : g.slug
    if (activeCategory === 'bryllup') return slug === 'bryllup-billeder'
    if (activeCategory === 'firmafest') return slug === 'firmafest-billeder'
    if (activeCategory === 'privatfest') return slug === 'privatfest-billeder'
    return slug === 'galleri-billeder'
  })

  // Fallback to all images from all galleries if main gallery not loaded yet
  const displayedImages = currentGallery?.images || [
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/selects-00-33-10-15-still012.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-8471.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-0003.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-9394.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-0027.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-8940.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/casperdj0184-ricofeldfoss-1_e7173f62.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-3466.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-0003_a63d675a.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-8471_4efa9672.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/sk-rmbillede-2022-01-05-155014.png',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/selects-01-45-15-03-still004.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/koldt.png',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/selects-00-33-10-15-still012_309df4ca.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-8471_25eccdb1.jpg',
    'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-1567.jpg'
  ]

  return (
    <div className="bg-neutral-950 text-white min-h-screen">
      <SEOHead title={title} description={description} />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 border-b border-neutral-900 bg-neutral-900/20 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs font-semibold tracking-widest text-gold-400 uppercase">
            Billeder &amp; Events
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            GALLERI
          </h1>
          <p className="text-neutral-300 text-sm sm:text-base max-w-xl mx-auto">
            Oplev stemningen fra tidligere fester, bryllupper og events med CasperDJ.
          </p>

          {/* Filter Tabs */}
          <div className="pt-6 flex flex-wrap justify-center gap-2">
            {[
              { id: 'alle', label: 'Alle billeder' },
              { id: 'bryllup', label: 'Bryllup' },
              { id: 'firmafest', label: 'Firmafest' },
              { id: 'privatfest', label: 'Privatfest' }
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCategory(tab.id)}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                  activeCategory === tab.id
                    ? 'bg-gold-400 text-neutral-950 shadow-md'
                    : 'bg-neutral-900 text-neutral-400 hover:text-white hover:bg-neutral-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-12 flex justify-center items-center">
              <div className="w-8 h-8 border-2 border-gold-400/30 border-t-gold-400 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {displayedImages.map((imgUrl, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative aspect-square sm:aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-900 border border-neutral-800 hover:border-gold-400/60 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold-400 cursor-pointer"
                >
                  <img
                    src={getOptimizedImageUrl(imgUrl, 600)}
                    alt={`Casper DJ feststemning ${idx + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                    <span className="text-xs font-semibold text-gold-400 flex items-center gap-1.5">
                      <i className="fa-regular fa-expand" aria-hidden="true" />
                      <span>Vis stort</span>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <Lightbox
          images={displayedImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : displayedImages.length - 1))}
          onNext={() => setLightboxIndex((prev) => (prev < displayedImages.length - 1 ? prev + 1 : 0))}
        />
      )}
    </div>
  )
}
